import {
    getSelectedCategoryPath,
    getSelectedProductPath,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as React from "react";
import { connect, DispatchProp } from "react-redux";

// eslint-disable-next-line spire/fenced-imports
import mixpanel from "mixpanel-browser";

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    mixPanelApiKey = "mixPanelApiKey",
}

const mapStateToProps = (state: any) => {
    const currentPage = getCurrentPage(state);
    const location = getLocation(state);

    return {
        currentPage,
        location,
        state,
    };
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    DispatchProp<any> & {
        fields: {
            [fields.mixPanelApiKey]: string;
        };
    };

const Analytics: React.FC<Props> = ({ currentPage, location, state, fields: { mixPanelApiKey }, dispatch }) => {
    React.useEffect(() => {
        mixpanel.init(mixPanelApiKey);
        (window as any).mixpanelInitialized = true;
    }, []);

    React.useEffect(() => {
        if (currentPage.type === "ProductListPage") {
            const categoryPath =
                getSelectedCategoryPath(state) ||
                (location?.pathname?.toLowerCase().startsWith("/content/") ? "" : location.pathname);
            const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
            mixpanel.track("Product Listing Pageview", {
                "Category Name": catalogPageState.value?.title,
            });
        } else if (currentPage.type === "ProductDetailsPage") {
            const location = getLocation(state);
            const productPath =
                getSelectedProductPath(state) ||
                (location?.pathname?.toLowerCase().startsWith("/content/") ? "" : location.pathname);
            const productState = getProductStateByPath(state, productPath)?.value;
            mixpanel.track("Product Detail Pageview", {
                "Product Name": productState?.productTitle,
            });
        } else if (currentPage.type === "CartPage") {
            mixpanel.track("Cart Pageview");
        } else {
            mixpanel.track("Default Pageview", {
                "Page Name": window.document.title,
            });
        }
    }, [currentPage]);

    return <></>;
};

const definition: WidgetDefinition = {
    group: "Common",
    displayName: "Analytics Widget",
    allowedContexts: ["Header"],
    fieldDefinitions: [
        {
            name: fields.mixPanelApiKey,
            displayName: "Mixpanel Api-Key",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(Analytics),
    definition,
};

export default widgetModule;
